/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from "react";
import { Skeleton, Tooltip } from "@mui/material";
import { RootStateOrAny, useSelector } from "react-redux";

import { CARGO_CODES } from "constants/permissions";
import { formatIntervaloSemana } from "utils/intervaloSemana";
import { AuthState } from "store/ducks/auth/types";
import { useListarPDV } from "services/api/AppWeb_Checkin/listar_pdv";
import {
  PerfilHierarquico,
  useUsuariosPorPerfilHierarquico,
} from "services/api/Identity_Usuario/usuarios-por-perfil-hierarquico";
import {
  IndicadorAgendamento,
  IndicadorAgendamentoPdv,
  IndicadorAgendamentoUsuario,
  TipoAgendamento,
  useIndicadoresAgendamentoPorNivel,
  useIndicadoresAgendamentoTotal,
} from "services/api/Common_Leads/indicadores-agendamento";
import * as IconsSVG from "assets/iconsSVG";
import * as S from "../performanceModal.styles";
import * as Local from "./styles";

interface Props {
  tipo: TipoAgendamento;
}

interface SelectOption {
  value: string;
  label: string;
}

interface MetricConfig {
  label: string;
  className?: string;
  icon: JSX.Element;
  getValue: (item: IndicadorAgendamento) => number;
  // Quando presente, exibe um badge de % ao lado do valor (ex.: conversão).
  getPercent?: (item: IndicadorAgendamento) => number;
}

// Nível imediatamente abaixo do cargo logado. Define tanto o TÍTULO da seção
// inferior ("Detalhes por ...") quanto o filtro de usuário exibido — sempre
// desce exatamente um nível na hierarquia:
//   Diretor         → Superintendente
//   Superintendente → Equipe (o cargo de gerente é sempre exibido como "Equipe")
//   Gerente         → Corretor
//   Corretor        → só vê o dele (sem filtro de usuário)
const getPerfilAbaixo = (
  codigoCargo2013: string | null,
  isDiretor: boolean,
  usuarioGerenteOnline: boolean
): { perfil: PerfilHierarquico; label: string } => {
  if (isDiretor || usuarioGerenteOnline) {
    return {
      perfil: PerfilHierarquico.Superintendente,
      label: "Superintendente",
    };
  }
  if (codigoCargo2013 === CARGO_CODES.SUPERINTENDENTE) {
    return { perfil: PerfilHierarquico.Gerente, label: "Equipe" };
  }
  return { perfil: PerfilHierarquico.Corretor, label: "Corretor" };
};

const AgendamentoDetails: React.FC<Props> = ({ tipo }) => {
  const { codigoCargo2013, usuarioDiretor, usuarioGerenteOnline } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);

  const [semanaAtual, setSemanaAtual] = useState(true);
  // Índice do card de métrica escolhido — realça a coluna correspondente nas
  // tabelas e esmaece as demais. Null = nenhum card selecionado.
  const [metricaSelecionada, setMetricaSelecionada] = useState<number | null>(
    null
  );
  const [pdvSelecionado, setPdvSelecionado] = useState<SelectOption | null>(
    null
  );
  const [usuarioSelecionado, setUsuarioSelecionado] =
    useState<SelectOption | null>(null);

  const isDiretor = usuarioDiretor === true;
  const isCorretor = codigoCargo2013 === CARGO_CODES.CORRETOR;
  // Corretor só vê os próprios dados — sem filtro de usuário.
  const temHierarquia = !isCorretor;
  const { perfil: perfilAbaixo, label: nivelNome } = getPerfilAbaixo(
    codigoCargo2013,
    isDiretor,
    usuarioGerenteOnline
  );

  const idPdv = pdvSelecionado?.value || null;
  const idUsuario = usuarioSelecionado?.value || null;
  const isVisita = tipo === TipoAgendamento.Visita;

  const metrics: MetricConfig[] = isVisita
    ? [
        {
          label: "Agendados da semana",
          className: "info",
          icon: <IconsSVG.CalendarTaskIcon />,
          getValue: (d) => d.agendamentosDaSemana ?? 0,
        },
        {
          label: "Visitas com agendamento",
          className: "success",
          icon: <IconsSVG.SuccessIconGreen />,
          getValue: (d) => d.visitasComAgendamento ?? 0,
          getPercent: (d) => d.percentualConversao ?? 0,
        },
        {
          label: "Visitas sem agendamento",
          className: "success",
          icon: <IconsSVG.SuccessIconGreen />,
          getValue: (d) => d.visitasSemAgendamento ?? 0,
        },
        {
          label: "Agendamentos para hoje",
          className: "warning",
          icon: <IconsSVG.ClockIcon />,
          getValue: (d) => d.agendamentosParaHoje ?? 0,
        },
      ]
    : [
        {
          label: "Agendados da semana",
          className: "info",
          icon: <IconsSVG.CalendarTaskIcon />,
          getValue: (d) => d.agendamentosDaSemana ?? 0,
        },
        {
          label: "Comparecimentos para assinatura",
          className: "success",
          icon: <IconsSVG.SuccessIconGreen />,
          getValue: (d) => d.comparecimento ?? 0,
          getPercent: (d) => d.percentualConversao ?? 0,
        },
        {
          label: "Agendamentos para hoje",
          className: "warning",
          icon: <IconsSVG.ClockIcon />,
          getValue: (d) => d.agendamentosParaHoje ?? 0,
        },
      ];

  const gridClass = isVisita ? "four" : "";

  // Filtros compõem a queryKey para refetch reativo.
  const filtroKey = `${tipo}-${semanaAtual}-${idPdv || ""}-${idUsuario || ""}`;

  const { data: dataPDV } = useListarPDV({
    queryKey: "agendamento-detalhes-pdvs-lista",
    enabled: true,
  });

  // Sempre um único select, do nível imediatamente abaixo do cargo logado.
  const { data: dataUsuariosAbaixo } = useUsuariosPorPerfilHierarquico({
    queryKey: `agendamento-detalhes-usuarios-${perfilAbaixo}`,
    perfil: perfilAbaixo,
    enabled: temHierarquia,
  });

  const { data: total, isFetching: isFetchingTotal } =
    useIndicadoresAgendamentoTotal({
      queryKey: `agendamento-total-${filtroKey}`,
      tipoAgendamento: tipo,
      semanaAtual,
      idPdv,
      idUsuario,
    });

  const { data: porNivel, isFetching: isFetchingNivel } =
    useIndicadoresAgendamentoPorNivel({
      queryKey: `agendamento-nivel-${filtroKey}`,
      tipoAgendamento: tipo,
      semanaAtual,
      idPdv,
      idUsuario,
    });

  const pdvOptions: SelectOption[] = useMemo(
    () =>
      dataPDV?.data?.map((item: any) => ({
        value: item.idPdv,
        label: item.nome,
      })) || [],
    [dataPDV]
  );

  const usuarioOptions: SelectOption[] = useMemo(
    () =>
      dataUsuariosAbaixo?.map((item) => ({
        value: item.idUsuario || item.id,
        label: item.nome,
      })) || [],
    [dataUsuariosAbaixo]
  );

  const temFiltro = Boolean(idPdv || idUsuario || metricaSelecionada !== null);
  const listaPdvs: IndicadorAgendamentoPdv[] = porNivel?.pdvs || [];
  const listaUsuarios: IndicadorAgendamentoUsuario[] = porNivel?.usuarios || [];
  const isLoadingNivel = isFetchingNivel && !porNivel;

  // Título só aparece quando há um recorte de PDV/usuário.
  const tituloContexto = pdvSelecionado
    ? `Indicadores em ${pdvSelecionado.label}`
    : usuarioSelecionado
    ? `Indicadores de ${usuarioSelecionado.label}`
    : "";

  const limparFiltros = () => {
    setPdvSelecionado(null);
    setUsuarioSelecionado(null);
    setMetricaSelecionada(null);
  };

  const renderColunaMetricasHeader = (rotulo: string, icone: JSX.Element) => (
    <S.HeaderGroup className={gridClass}>
      <S.HeaderCell className="label">
        {icone}
        {rotulo}
      </S.HeaderCell>
      {metrics.map((metric) => (
        <S.HeaderCell
          key={metric.label}
          className={metric.className}
          title={metric.label}
        >
          {metric.icon}
        </S.HeaderCell>
      ))}
    </S.HeaderGroup>
  );

  // Célula de métrica (pill) com valor e, se aplicável, badge de %.
  // Realce/esmaecimento seguem o card de métrica selecionado.
  const renderMetricCell = (
    metric: MetricConfig,
    item: IndicadorAgendamento,
    index: number
  ) => {
    const realcada = metricaSelecionada === index;
    const esmaecida = metricaSelecionada !== null && !realcada;

    return (
      <S.Metric
        key={metric.label}
        className={`${metric.className} ${realcada ? "highlight" : ""} ${
          esmaecida ? "muted" : ""
        }`}
        as="div"
      >
        {metric.getValue(item)}
        {metric.getPercent ? (
          <S.PercentPill>{metric.getPercent(item)}%</S.PercentPill>
        ) : null}
      </S.Metric>
    );
  };

  return (
    <S.Content>
      <S.FixedSection>
        {/* Filtros PDV + usuário (+ equipe p/ gerente) + semana, em uma linha */}
        <Local.FiltersRow>
          <Local.FilterField>
            <S.Autocomplete
              fullWidth
              id="agendamento-detalhes-pdv"
              size="small"
              options={pdvOptions}
              getOptionLabel={(option: any) => option.label || ""}
              // @ts-ignore
              getOptionSelected={(option: any, value: any) =>
                option.value === value.value
              }
              value={pdvSelecionado}
              onChange={(_event: any, value: any) =>
                setPdvSelecionado((value as SelectOption) || null)
              }
              renderInput={(params) => (
                <S.TextField
                  {...params}
                  placeholder="Selecionar PDV"
                  variant="outlined"
                />
              )}
            />
          </Local.FilterField>

          {temHierarquia ? (
            <Local.FilterField>
              <S.Autocomplete
                fullWidth
                id="agendamento-detalhes-usuario"
                size="small"
                options={usuarioOptions}
                getOptionLabel={(option: any) => option.label || ""}
                // @ts-ignore
                getOptionSelected={(option: any, value: any) =>
                  option.value === value.value
                }
                value={usuarioSelecionado}
                onChange={(_event: any, value: any) =>
                  setUsuarioSelecionado((value as SelectOption) || null)
                }
                renderInput={(params) => (
                  <S.TextField
                    {...params}
                    placeholder={`Selecionar ${nivelNome}`}
                    variant="outlined"
                  />
                )}
              />
            </Local.FilterField>
          ) : null}

          <Local.WeekToggle>
            <Local.WeekButton
              type="button"
              active={semanaAtual}
              onClick={() => setSemanaAtual(true)}
            >
              Semana atual ({formatIntervaloSemana(true)})
            </Local.WeekButton>
            <Local.WeekButton
              type="button"
              active={!semanaAtual}
              onClick={() => setSemanaAtual(false)}
            >
              Semana anterior ({formatIntervaloSemana(false)})
            </Local.WeekButton>
          </Local.WeekToggle>
        </Local.FiltersRow>

        {/* Título/limpar só aparecem com algum filtro ativo */}
        {temFiltro ? (
          <Local.TitleRow>
            {tituloContexto ? (
              <Local.FilterTitle>{tituloContexto}</Local.FilterTitle>
            ) : null}
            <Local.ClearButton type="button" onClick={limparFiltros}>
              <IconsSVG.CloseIcon width={12} height={12} />
              Limpar filtros
            </Local.ClearButton>
          </Local.TitleRow>
        ) : null}

        {/* Cards de resumo — clicar seleciona a métrica */}
        <S.SummaryGrid className={isVisita ? "four" : ""}>
          {metrics.map((metric, index) => {
            const selecionado = metricaSelecionada === index;
            const esmaecido = metricaSelecionada !== null && !selecionado;

            return (
              <S.SummaryCard
                key={metric.label}
                type="button"
                className={`${metric.className} ${
                  selecionado ? "selected" : ""
                } ${esmaecido ? "muted" : ""}`}
                onClick={() =>
                  setMetricaSelecionada(selecionado ? null : index)
                }
              >
                {selecionado ? (
                  <S.SummaryCheck>
                    <IconsSVG.CheckWhiteIcon />
                  </S.SummaryCheck>
                ) : null}
                <S.SummaryIcons>
                  {metric.icon}
                  {!isVisita ? (
                    <S.TypeIcon>
                      <IconsSVG.TagLead />
                    </S.TypeIcon>
                  ) : null}
                </S.SummaryIcons>
                <S.SummaryNumber>
                  {isFetchingTotal && !total
                    ? "--"
                    : total
                    ? metric.getValue(total)
                    : "--"}
                  {total && metric.getPercent ? (
                    <S.SummaryPercent>
                      {metric.getPercent(total)}%
                    </S.SummaryPercent>
                  ) : null}
                </S.SummaryNumber>
                <S.SummaryLabel>{metric.label}</S.SummaryLabel>
              </S.SummaryCard>
            );
          })}
        </S.SummaryGrid>

        {/* Cabeçalho tabela por PDV */}
        <S.TableHeader style={{ marginTop: 16 }}>
          {renderColunaMetricasHeader("PDV's", <IconsSVG.IndicatorStoreIcon />)}
          {renderColunaMetricasHeader("PDV's", <IconsSVG.IndicatorStoreIcon />)}
        </S.TableHeader>
      </S.FixedSection>

      <S.ScrollArea>
        {/* Tabela por PDV */}
        {isLoadingNivel && (
          <S.BrokerList>
            {[0, 1, 2, 3].map((item) => (
              <Skeleton key={item} variant="rounded" width="100%" height={72} />
            ))}
          </S.BrokerList>
        )}

        {!isLoadingNivel && listaPdvs.length > 0 && (
          <S.BrokerList>
            {listaPdvs.map((pdv) => {
              const selecionado = idPdv === pdv.idPdv;
              const dimmed = Boolean(idPdv) && !selecionado;
              const Av = selecionado ? S.AvatarSelected : S.Avatar;
              return (
                <S.BrokerCard
                  key={pdv.idPdv}
                  className={`${gridClass} clickable ${
                    selecionado ? "selected" : ""
                  } ${dimmed ? "dimmed" : ""}`}
                  onClick={() =>
                    setPdvSelecionado(
                      selecionado
                        ? null
                        : { value: pdv.idPdv, label: pdv.nomePdv }
                    )
                  }
                >
                  <S.BrokerInfo>
                    <Av>
                      <IconsSVG.IndicatorStoreIcon />
                    </Av>
                    <div style={{ minWidth: 0 }}>
                      <Tooltip title={pdv.nomePdv || ""} placement="top" arrow>
                        <S.BrokerName>{pdv.nomePdv}</S.BrokerName>
                      </Tooltip>
                    </div>
                  </S.BrokerInfo>
                  {metrics.map((metric, index) =>
                    renderMetricCell(metric, pdv, index)
                  )}
                </S.BrokerCard>
              );
            })}
          </S.BrokerList>
        )}

        {!isLoadingNivel && listaPdvs.length === 0 && (
          <S.Empty>Nenhum agendamento no período</S.Empty>
        )}

        {/* Detalhes por usuário (nível abaixo) */}
        {listaUsuarios.length > 0 && (
          <>
            <Local.SectionDivider>
              <IconsSVG.IndicatorUserIcon />
              Detalhes por {nivelNome}
            </Local.SectionDivider>

            <S.TableHeader style={{ marginBottom: 14 }}>
              {renderColunaMetricasHeader(
                nivelNome,
                <IconsSVG.IndicatorUserIcon />
              )}
              {renderColunaMetricasHeader(
                nivelNome,
                <IconsSVG.IndicatorUserIcon />
              )}
            </S.TableHeader>

            <S.BrokerList>
              {listaUsuarios.map((usuario) => {
                const selecionado = idUsuario === usuario.idUsuario;
                const dimmed = Boolean(idUsuario) && !selecionado;
                const Av = selecionado ? S.AvatarSelected : S.Avatar;
                return (
                  <S.BrokerCard
                    key={usuario.idUsuario}
                    className={`${gridClass} clickable ${
                      selecionado ? "selected" : ""
                    } ${dimmed ? "dimmed" : ""}`}
                    onClick={() =>
                      setUsuarioSelecionado(
                        selecionado
                          ? null
                          : {
                              value: usuario.idUsuario,
                              label: usuario.nomeUsuario,
                            }
                      )
                    }
                  >
                    <S.BrokerInfo>
                      {/* Foto do usuário quando disponível; o avatar "selected"
                          tinge o ícone de branco, então só se aplica ao fallback. */}
                      {usuario.urlFoto ? (
                        <S.Avatar>
                          <img
                            src={usuario.urlFoto}
                            alt={usuario.nomeUsuario || ""}
                          />
                        </S.Avatar>
                      ) : (
                        <Av>
                          <IconsSVG.UserIconMin />
                        </Av>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <Tooltip
                          title={usuario.nomeUsuario || ""}
                          placement="top"
                          arrow
                        >
                          <S.BrokerName>{usuario.nomeUsuario}</S.BrokerName>
                        </Tooltip>
                      </div>
                    </S.BrokerInfo>
                    {metrics.map((metric, index) =>
                      renderMetricCell(metric, usuario, index)
                    )}
                  </S.BrokerCard>
                );
              })}
            </S.BrokerList>
          </>
        )}
      </S.ScrollArea>
    </S.Content>
  );
};

export default AgendamentoDetails;
